import { BalanceData, GearItem, GrantedItems, RARITY_IDS } from "@highmarch/domain";
import { fmtCompact } from "../kit";
import { wellHtml } from "../components";
import { gearIconUrl, resourceIconUrl, uiUrl } from "../portraits";
import { dealsLink } from "./dealsLink";
import { fmtMinutes } from "./dealsModel";
import { artUrl } from "./dealsTiles";
import { SHOP_SKIN } from "../shopSkin";
import { rewardChest } from "../chestReveal";

/**
 * The "DELIVERED!" receipt after a Deals or VIP purchase (and the VIP bundle). Its own module so the VIP page can show
 * it without importing the Deals screen, which imports the VIP page (no import cycle).
 */

/**
 * What arrived, as small wells (design system v2): gems, gold and speed-ups in the navy well, each chest opened
 * (its rolled rarity) and each gear item in its rarity's jewel well.
 */
export function receiptTilesHtml(balance: BalanceData, g: GrantedItems, bankedGear: readonly GearItem[]): string {
  const tile = (icon: string, rarity: number | null, count: string, name: string, cap = "") =>
    wellHtml({ art: icon, rarity, size: "sm", count, cap, tag: "span", className: "k-pop", data: `title="${name}" role="img" aria-label="${name}${count ? ` ${count}` : ""}"` });
  const parts: string[] = [];
  const gold = g.gold + g.chests.reduce((a, c) => a + c.gold, 0);
  if (g.gems > 0) parts.push(tile(artUrl("hq:icons/res_gems.png"), null, fmtCompact(g.gems), "Gems"));
  if (gold > 0) parts.push(tile(resourceIconUrl("gold"), null, fmtCompact(gold), "Gold"));
  const minutes = g.speedUps.reduce((a, m) => a + m, 0) + g.chests.reduce((a, c) => a + c.speedUpMinutes, 0);
  if (minutes > 0) parts.push(tile(artUrl("hq:icons/res_speedup.png"), null, "", "Speed-ups", fmtMinutes(minutes)));
  for (const c of g.chests) parts.push(tile(artUrl(`hq:icons/chest_${RARITY_IDS[c.rarity]}.png`), c.rarity, "", `${RARITY_IDS[c.rarity]} chest`));
  for (const it of [...g.gear, ...bankedGear]) {
    const slot = balance.gear.slots[it.slot]?.id ?? "mount";
    parts.push(tile(gearIconUrl(slot, RARITY_IDS[it.rarity]), it.rarity, "", `${RARITY_IDS[it.rarity]} ${slot}`));
  }
  for (const s of g.skins) parts.push(tile(uiUrl(`skins/${s.replace(".", "_")}.png`), null, "", s));
  return parts.join("");
}

/**
 * A purchase with chests in it: each chest opens on the chest stage first (the battle chest's slot machine — the drop,
 * TAP, the burst, its cards), one after another, then the receipt lists everything. The banked gear arrives in chest
 * order (`LevelCatalog.bankChest` per chest), so each chest shows its own `gearCount` items.
 */
export function revealThenReceipt(parent: HTMLElement, title: string, g: GrantedItems, bankedGear: readonly GearItem[]): void {
  const l = dealsLink();
  if (!l || g.chests.length === 0) { showReceipt(parent, title, g, bankedGear); return; }
  let k = 0, at = 0;
  const n = g.chests.length;
  const next = (): void => {
    if (k >= n) { showReceipt(parent, title, g, bankedGear); return; }
    const c = g.chests[k++];
    const gear = bankedGear.slice(at, at + c.gearCount);
    at += c.gearCount;
    rewardChest(parent, l.catalog.balance, c.rarity, { gear: [...gear], speedUpMinutes: c.speedUpMinutes, gold: c.gold }, `${title}${n > 1 ? ` · ${k}/${n}` : ""}`, next);
  };
  next();
}

export function showReceipt(parent: HTMLElement, title: string, g: GrantedItems, bankedGear: readonly GearItem[]): void {
  const l = dealsLink();
  if (!l) return;
  const gear = g.gear.length + bankedGear.length, chests = g.chests.length;
  const scrim = document.createElement("div");
  scrim.className = `scrim dreceipt ${SHOP_SKIN}`;
  scrim.innerHTML = `<div class="panel dialog econdlg drec"><div class="drays" aria-hidden="true"></div><div class="ribbon disp dtitle">DELIVERED!</div>
    <p class="drname"><b>${title}</b></p><div class="rtiles">${receiptTilesHtml(l.catalog.balance, g, bankedGear)}</div>
    <p class="rnote">${[chests > 0 ? `${chests} chest${chests === 1 ? "" : "s"} opened` : "", gear > 0 ? `${gear} gear item${gear === 1 ? "" : "s"} in your Armoury, bound to you` : "", g.skins.length > 0 ? "New skin waiting in Skins" : ""].filter(Boolean).join(" · ")}</p>
    <button class="btn gold ok disp accent"><span class="lbl"><span class="disp">GREAT!</span></span></button></div>`;
  const done = () => scrim.remove();
  scrim.querySelector<HTMLButtonElement>(".ok")!.onclick = done;
  scrim.onclick = (e) => { if (e.target === scrim) done(); };
  parent.append(scrim);
}
